/**
 * Run detail (F2): the drill-down page for a single run — /runs/:id.
 *
 * PageHeader (state, pipeline, full id, timestamps, launcher), a GOV.UK
 * error-summary alert that anchors to failed/controlled steps, the step
 * timeline (attempt, duration, rows in→out, unmatched, collapsible error
 * detail), the produced artifacts, an inline approval affordance while the
 * run waits at a checkpoint, and a jump into the audit assistant scoped to
 * this run. Live-polls through useJobPolling while non-terminal.
 */
import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ReplayIcon from "@mui/icons-material/Replay";
import { api, ApiError, type PendingApproval, type StepState } from "../lib/api";
import { errorText } from "../lib/errors";
import { useToast } from "../hooks/useToast";
import { ID_SLICE_LEN } from "../lib/config";
import { useApiQuery } from "../hooks/useApiQuery";
import { useJobPolling } from "../hooks/useJobPolling";
import { usePageTitle } from "../hooks/usePageTitle";
import { categoryOf, pollIntervalMs } from "../lib/states";
import {
  formatDateTime,
  formatDuration,
  stepAnchorId,
  stepDurationMs,
  summarySteps,
} from "../lib/runs";
import { ERROR_DETAIL_MAX_CHARS } from "../lib/config";
import { PageHeader } from "../components/ui/PageHeader";
import { SectionHeader, SectionLabel } from "../components/ui/SectionLabel";
import { StateChip } from "../components/ui/StateChip";
import { ErrorAlert } from "../components/ui/ErrorAlert";
import { CopyIdButton } from "../components/ui/CopyIdButton";
import { ArtifactFiles } from "../components/ArtifactFiles";
import { ApprovalDialog } from "../components/ApprovalDialog";
import { RunControlsPanel } from "../components/RunControlsPanel";

/**
 * Relaunch a failed / controls-blocked run as a NEW job, behind an explicit
 * confirmation. Pre-upgrade runs never recorded their input: the 409 names
 * the tool and we route to a fresh launch instead of guessing a file.
 */
function RetryRunButton({ jobId }: { jobId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [launching, setLaunching] = useState(false);

  async function confirm() {
    setLaunching(true);
    try {
      const started = await api.retryJob(jobId);
      toast(t("auditActions.rerunStarted"), "success");
      setOpen(false);
      void navigate(`/runs/${encodeURIComponent(started.job_id)}`);
    } catch (err) {
      const toolKey = (err instanceof ApiError ? (err.body as { tool?: string }).tool : undefined);
      if (err instanceof ApiError && err.status === 409 && toolKey) {
        toast(errorText(err), "warning");
        setOpen(false);
        void navigate(`/tool?select=${encodeURIComponent(toolKey)}`);
      } else {
        toast(errorText(err), "error");
      }
    } finally {
      setLaunching(false);
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ReplayIcon />}
        onClick={() => setOpen(true)}
      >
        {t("auditActions.retryJob")}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("auditActions.confirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("auditActions.confirmRetryBody", { id: jobId.slice(0, ID_SLICE_LEN) })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={launching}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={() => void confirm()} disabled={launching}>
            {launching ? t("auditActions.launching") : t("auditActions.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function RunDetailPage() {
  const { t, i18n } = useTranslation();
  usePageTitle("runDetail.title");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [reviewing, setReviewing] = useState<PendingApproval | null>(null);

  const { job, error, isLoading, refetch, terminal } = useJobPolling(id, {
    // The list/detail pages are read views — completion toasts belong to
    // the launching surfaces (tool page, wizard), not to a drill-down.
    toastOnTerminal: false,
  });

  // Inline approval affordance while the run waits at a checkpoint.
  const waiting = job ? categoryOf(job.state) === "waiting" : false;
  const pendingQuery = useApiQuery(["approvals", "pending"], api.pending, {
    pollMs: pollIntervalMs(),
    enabled: waiting,
  });
  const pendingHere = useMemo(
    () => (pendingQuery.data ?? []).filter((p) => p.job_id === id),
    [pendingQuery.data, id],
  );

  const attention = useMemo(() => (job ? summarySteps(job) : []), [job]);

  if (isLoading) return <LinearProgress />;
  if (!job) {
    return <ErrorAlert error={error} onRetry={() => void refetch()} />;
  }

  // the assistant, scoped to this run — placed where the operator looks
  // for the result (the produced files), not at the foot of the page
  const askAudit = (
    <Button
      variant="contained"
      size="small"
      startIcon={<QuestionAnswerIcon />}
      onClick={() => navigate(`/audit?job=${encodeURIComponent(job.job_id)}`)}
    >
      {t("runDetail.askAudit")}
    </Button>
  );

  return (
    <Stack spacing={3}>
      <Box>
        <Link
          component={RouterLink}
          to="/runs"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 14 }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} /> {t("runDetail.back")}
        </Link>
      </Box>

      <PageHeader
        title={job.dag_name}
        extra={
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <StateChip state={job.state} fallbackLabel={job.dashboard_status} />
              <Typography variant="body2" className="num" color="text.secondary">
                {job.job_id}
              </Typography>
              <CopyIdButton value={job.job_id} />
              {["failure", "warning"].includes(categoryOf(job.state) ?? "") && (
                <RetryRunButton jobId={job.job_id} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t("runDetail.created")}: {formatDateTime(job.created_at, i18n.language)}
              {" · "}
              {t("runDetail.finished")}:{" "}
              {formatDateTime(job.finished_at, i18n.language)}
              {job.created_by ? ` · ${t("runDetail.launchedBy")}: ${job.created_by}` : ""}
            </Typography>
            {job.forked_from_job_id && (
              <Typography variant="caption" color="text.secondary">
                <CallSplitIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.5 }} />
                {t("runDetail.forkedFrom")}{" "}
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  className="num"
                  onClick={() => navigate(`/runs/${job.forked_from_job_id}`)}
                >
                  {job.forked_from_job_id.slice(0, ID_SLICE_LEN)}
                </Link>
              </Typography>
            )}
            {(job.forks?.length ?? 0) > 0 && (
              <Typography variant="caption" color="text.secondary">
                <CallSplitIcon sx={{ fontSize: 14, verticalAlign: "text-bottom", mr: 0.5 }} />
                {t("runDetail.forks", { count: job.forks?.length ?? 0 })}{" "}
                {job.forks?.map((fork, index) => (
                  <span key={fork.job_id}>
                    {index > 0 ? ", " : ""}
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      className="num"
                      onClick={() => navigate(`/runs/${fork.job_id}`)}
                    >
                      {fork.job_id.slice(0, ID_SLICE_LEN)}
                    </Link>
                  </span>
                ))}
              </Typography>
            )}
          </Stack>
        }
      />

      {!terminal && <LinearProgress />}

      {/* GOV.UK-style error summary: one alert at the top, its links scroll
          to the steps concerned. */}
      {attention.length > 0 && (
        <Alert
          severity={categoryOf(job.state) === "warning" ? "warning" : "error"}
        >
          <AlertTitle>
            {t("runDetail.errorSummary", { count: attention.length })}
          </AlertTitle>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            {t("runDetail.errorSummaryHint")}
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
            {attention.map((step) => (
              <li key={step.step_key}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  color="inherit"
                  sx={{ fontWeight: 600 }}
                  onClick={() =>
                    document
                      .getElementById(stepAnchorId(step.step_key))
                      ?.scrollIntoView({ behavior: "smooth", block: "center" })
                  }
                >
                  {step.step_key} — {step.tool_name}
                </Link>
              </li>
            ))}
          </Stack>
        </Alert>
      )}

      {waiting && pendingHere.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReviewing(pendingHere[0])}
            >
              {t("wizard.review")}
            </Button>
          }
        >
          {t("runDetail.awaiting")}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* a run without produced files (failed early) keeps the assistant
              reachable from its steps */}
          {job.artifacts.length > 0 ? (
            <SectionLabel>{t("runDetail.steps")}</SectionLabel>
          ) : (
            <SectionHeader label={t("runDetail.steps")} action={askAudit} />
          )}
          <Stack spacing={0} sx={{ mt: 1 }}>
            {job.steps.map((step) => (
              <StepTimelineRow key={step.step_key} step={step} />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* The Controls sheet, read out of the produced workbook — with the
          agent-assisted fix & re-process loop when controls failed. */}
      {terminal && job.artifacts.length > 0 && <RunControlsPanel jobId={job.job_id} />}

      {job.artifacts.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader label={t("files.produced")} action={askAudit} />
            <ArtifactFiles artifacts={job.artifacts} />
          </CardContent>
        </Card>
      )}

      {reviewing && (
        <ApprovalDialog
          item={reviewing}
          onClose={() => setReviewing(null)}
          onDecided={() => {
            void pendingQuery.refetch();
            void refetch();
          }}
        />
      )}
    </Stack>
  );
}

function StepTimelineRow({ step }: { step: StepState }) {
  const { t } = useTranslation();
  const [showError, setShowError] = useState(false);
  const duration = stepDurationMs(step.started_at, step.finished_at);
  const hasRowsIn = typeof step.rows_in === "number" && step.rows_in > 0;

  return (
    <Box
      id={stepAnchorId(step.step_key)}
      sx={{
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-child": { border: 0 },
        scrollMarginTop: 96,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {step.step_key}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {step.tool_name}
            {step.rule_version ? ` · ${step.rule_version}` : ""}
            {typeof step.attempt === "number" && step.attempt > 1
              ? ` · ${t("runDetail.attempt", { count: step.attempt })}`
              : ""}
          </Typography>
          {step.error_detail && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                color="error"
                onClick={() => setShowError(!showError)}
                startIcon={showError ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={showError}
              >
                {showError ? t("runDetail.hideError") : t("runDetail.showError")}
              </Button>
              <Collapse in={showError}>
                <Typography
                  component="pre"
                  variant="caption"
                  className="num"
                  sx={{
                    color: "error.main",
                    whiteSpace: "pre-wrap",
                    m: 0,
                    mt: 0.5,
                    p: 1,
                    bgcolor: "background.default",
                    borderRadius: 1,
                    overflowX: "auto",
                  }}
                >
                  {step.error_detail.slice(0, ERROR_DETAIL_MAX_CHARS)}
                </Typography>
              </Collapse>
            </Box>
          )}
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} flexShrink={0}>
          <StateChip state={step.state} />
          {duration !== null && (
            <Typography variant="caption" className="num" color="text.secondary">
              {formatDuration(duration)}
            </Typography>
          )}
          {hasRowsIn ? (
            <Typography variant="caption" className="num" color="text.secondary">
              {t("runDetail.rows", { in: step.rows_in, out: step.rows_out })}
            </Typography>
          ) : (
            step.rows_out > 0 && (
              <Typography variant="caption" className="num" color="text.secondary">
                {t("files.rows", { count: step.rows_out })}
              </Typography>
            )
          )}
          {step.unmatched_rows > 0 && (
            <Typography variant="caption" className="num" sx={{ color: "warning.main" }}>
              {t("files.unmatchedShort", { count: step.unmatched_rows })}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
